import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const SpreadsheetPreview = ({ file, url }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Helper function to safely format cell values
    const formatCellValue = (value) => {
        if (value === null || value === undefined) {
            return '';
        }

        // Handle Date objects
        if (value instanceof Date) {
            // Ensure consistent date formatting
            try {
                return value.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            } catch (e) {
                return value.toLocaleString(); // Fallback
            }
        }

        // Handle other objects
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (e) {
                return '[Object]';
            }
        }

        return String(value);
    };

    useEffect(() => {
        const loadFile = async () => {
            if (!file || !url) {
                setError('File or URL missing');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null); // Reset error on new load
                setData(null);  // Reset data on new load

                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const blob = await response.blob();

                // Extract file extension
                const fileName = file.name || '';
                const fileExtension = fileName.split('.').pop()?.toLowerCase();

                const handleParseComplete = (results) => {
                    if (results.errors && results.errors.length > 0) {
                        console.error('PapaParse Errors:', results.errors);
                        setError(`Error parsing file: ${results.errors[0].message}`);
                        setIsLoading(false);
                        return;
                    }
                    // Ensure meta and fields exist
                    if (!results.meta || !results.meta.fields || results.meta.fields.length === 0) {
                        setError('No headers found or file is empty');
                        setIsLoading(false);
                        return;
                    }

                    const headers = results.meta.fields;
                    // Transform results.data (array of objects) into an array of arrays
                    const rowsData = results.data.slice(0, 100).map(dataObject => {
                        // Map headers to their corresponding values in the object
                        return headers.map(header => dataObject[header]);
                    });

                    setData({
                        headers: headers,
                        rows: rowsData // Use the transformed array of arrays
                    });
                    setIsLoading(false);
                };

                const handleParseError = (parseError) => {
                    console.error('Error parsing file:', parseError);
                    setError('Error parsing file');
                    setIsLoading(false);
                };

                // Handle CSV, DSV, TSV using PapaParse
                if (file.mime_type === 'text/csv' || ['csv', 'dsv', 'tsv'].includes(fileExtension)) {
                    const text = await blob.text();
                    Papa.parse(text, {
                        // header: true, // Let's parse as arrays directly
                        dynamicTyping: true,
                        skipEmptyLines: 'greedy',
                        complete: (results) => {
                            if (results.errors && results.errors.length > 0) {
                                console.error('PapaParse Errors:', results.errors);
                                setError(`Error parsing file: ${results.errors[0].message}`);
                                setIsLoading(false);
                                return;
                            }
                            // Ensure data exists and has at least a header row
                            if (!results.data || results.data.length === 0) {
                                setError('File is empty or could not be parsed');
                                setIsLoading(false);
                                return;
                            }

                            // First row is headers, the rest is data
                            const rawHeaders = results.data[0];
                            // Ensure headers are strings, provide default if empty/null
                            const headers = rawHeaders.map((h, index) => formatCellValue(h) || `Column ${index + 1}`);
                            const rowsData = results.data.slice(1, 101); // Get next 100 rows

                            setData({
                                headers: headers,
                                rows: rowsData
                            });
                            setIsLoading(false);
                        },
                        error: handleParseError,
                        // Delimiter detection is automatic
                    });
                }
                // Handle Excel files (xlsx, xls)
                else if (['xlsx', 'xls'].includes(fileExtension) || file.mime_type.includes('spreadsheetml') || file.mime_type.includes('excel')) {
                    const buffer = await blob.arrayBuffer();
                    const workbook = XLSX.read(buffer, {
                        type: 'array',
                        cellDates: true, // Attempt to parse dates
                        // cellNF: true // Keep original number formats (can cause issues)
                    });

                    const firstSheetName = workbook.SheetNames[0];
                    if (!firstSheetName) {
                        setError('Excel file contains no sheets');
                        setIsLoading(false);
                        return;
                    }
                    const firstSheet = workbook.Sheets[firstSheetName];

                    // Generate JSON data with headers from the first row
                    // Use defval: '' to ensure missing cells become empty strings
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                        header: 1, // Get arrays of values, first array is header
                        defval: '', // Use empty string for blank cells
                        raw: false, // Format values (e.g., dates)
                        dateNF: 'yyyy-mm-dd' // Specify date format if cellDates is true
                    });

                    if (!jsonData || jsonData.length === 0) {
                        setError('No data found in Excel file');
                        setIsLoading(false);
                        return;
                    }

                    // Ensure headers are valid strings, replace null/undefined with Column X
                    const headers = (jsonData[0] || []).map((header, index) =>
                        formatCellValue(header) || `Column ${index + 1}`
                    );

                    // Process rows, ensuring each row matches header length
                    const rows = jsonData.slice(1, 101).map(rowArray => {
                        const rowObject = {};
                        // Use headers array directly for consistent keys
                        headers.forEach((header, index) => {
                            // Map array elements to header keys
                            rowObject[header] = rowArray[index];
                        });

                        // Convert row data to match PapaParse structure (array of values)
                        const rowValues = headers.map(header => rowObject[header]);

                        // Pad row if shorter than headers (shouldn't happen often with defval)
                        // while (rowValues.length < headers.length) {
                        //     rowValues.push('');
                        // }
                        return rowValues;
                    });


                    setData({
                        headers: headers,
                        rows: rows
                    });
                    setIsLoading(false);
                } else {
                    setError(`Unsupported file type: .${fileExtension} or MIME type: ${file.mime_type}`);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('Error loading or processing spreadsheet:', err);
                setError(`Error loading file: ${err.message}`);
                setIsLoading(false);
            }
        };

        loadFile();

        // Cleanup function (optional)
        return () => {
            // Any cleanup logic if needed
        };
    }, [file, url]); // Dependencies for the effect

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">{error}</div>
            </div>
        );
    }

    if (!data || !data.headers || !data.rows) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">No data available</div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                    <tr>
                        {data.headers.map((header, index) => (
                            <th
                                key={index}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                            >
                                {formatCellValue(header)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {/* Ensure we iterate based on headers length for consistency */}
                            {data.headers.map((header, colIndex) => (
                                <td
                                    key={`${rowIndex}-${header}-${colIndex}`} // Use header in key for stability
                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                >
                                    {/* Access row data by index */}
                                    {formatCellValue(row[colIndex])}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {data.rows.length >= 100 && ( // Show message if row count is 100 or more
                <div className="p-4 text-center text-sm text-gray-500">
                    Showing first 100 rows
                </div>
            )}
        </div>
    );
};

export default SpreadsheetPreview;